import {
  PaletteOptions,
  ThemeOptions,
  alpha,
  createTheme,
  darken,
  lighten,
} from '@mui/material/styles'

import rem, { getWhiteOrBlackTextColorByBackground } from '~/utils/ui'

export const DEFAULT_PALETTE: PaletteOptions = {
  marketing: {
    border1: '#CCCCCC',
    border2: '#E1E4EA',
    otherButtonBackground: '#504685',
  },
  menuBackground: '#001433',
  menuIcon: '#C1C6FF',
  menuFont: '#FFFFFF',
  mainFont: '#001433', // subtitles or accent texts
  mainBackground: '#F5F6FF',
  primary1: '#63ED80', // used only for Replify marketing pages (it's the green color)
  primary2: '#63ED80', // only on marketing pages
  secondary1: '#00245C', // icons, chart color
  secondary2: '#001433', // used for some libby branding, not configurable
  tertiary1: '#3942A8', //  links, chart color
  tertiary2: '#C1C6FF', // not used anymore
  tertiary3: '#F5F6FF',
  tertiary4: '#8D8D8D',
  tertiary5: '#E5E5E5',
  primaryButtonBg: '#63ED80',
  primaryButtonFont: '#010101',
  secondaryButtonBg: '#FFFFFF',
  secondaryButtonFont: '#001433',
  tertiaryButtonBg: '#00245C',
  tertiaryButtonFont: '#FFFFFF',
  error: { main: '#FF5555' },
  deleteButton: {
    main: '#d32f2f',
    dark: '#b71c1c',
  },
  demoArrowColor: '#FFD21D',

  github: {
    green: '#28a745', // GitHub green
    red: '#d73a49', // GitHub red
    black: '#24292e', // GitHub black
  },

  onboarding: {
    mainFont: '#FFFFFF',
    mainBg: '#001433',
    tryItOut: {
      titleFont: '#63ED80',
      dottedLineBorder: '#3942A8',
      mainButtonBg: '#63ED80',
      mainButtonFont: '#011433',
      secondaryButtonBg: '#FFFFFF',
      secondaryButtonFont: '#011433',
      primaryCtaFont: '#63ED80',
      ctaBoxesBg: '#FFFFFF',
      ctaBoxesAndIconFont: '#011433',
      secondaryFont: '#FFFFFF',
      tertiaryFont: '#63ED80',
      linkFont: '#C1C6FF',
    },
  },

  admin: {
    color1: '#F50057',
    color2: '#FFB3CB',
    mainBg: '#2B1119',
  },

  analytics: {
    // color1: primary green / mint
    color1: '#00a63e',
    // color2: primary blue
    color2: '#155dfc',
    // color3: purple
    color3: '#9810fa',
    // color4: indigo
    color4: '#4f39f6',
    // color5: warm orange
    color5: '#e18528',
    // color6: turquoise
    color6: '#009689',
  },
}

const baseTheme = createTheme({
  typography: {
    fontFamily: ['Archivo', 'sans-serif'].join(','),
    body1: {
      fontFamily: ['Archivo', 'sans-serif'].join(','),
    },
    fontWeightMedium: 600,
  },
  palette: {
    ...DEFAULT_PALETTE,
    common: {
      // this is not part of overridable defaults, it is constant.
      zircon: '#F5F6FF',
      midnight: '#001433',
      white: '#FFFFFF',
    },
  },
  mixins: {
    toolbar: {
      minHeight: 72,
      '@media (min-width:0px) and (orientation: landscape)': {
        minHeight: 64,
      },
      '@media (min-width:600px)': {
        minHeight: 96,
      },
    },
    accountToolbar: {
      '@media (min-width:0px)': {
        minHeight: 'initial',
        maxHeight: 56,
      },
    },
  },
  breakpoints: {
    values: {
      xxxs: 0,
      xxs: 300,
      xs: 400,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
})

const theme = createTheme(baseTheme, {
  components: {
    MuiInput: {
      styleOverrides: {
        root: ({ theme: themeParam }) => ({
          color: themeParam.palette.common.black,
          '& .Mui-disabled.MuiInputBase-input': {
            WebkitTextFillColor: themeParam.palette.tertiary1,
          },
          '&.MuiInputBase-root.MuiAutocomplete-inputRoot': {
            paddingRight: '2px',
            paddingBlock: '4px',
          },
        }),
        underline: ({ theme: themeParam }) => ({
          '&:before': {
            borderBottom: `1px solid ${themeParam.palette.common.black}`,
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: `2px solid ${themeParam.palette.tertiary1}`,
          },
          '&.Mui-focused:after': {
            borderBottom: `2px solid ${themeParam.palette.tertiary1}`,
          },
          '&.Mui-error:not(.Mui-disabled):before, &.Mui-error:not(.Mui-disabled):after':
            {
              borderBottom: `2px solid ${themeParam.palette.error.main}`,
            },
          '&.Mui-disabled': {
            '&::before': {
              borderBottom: 'none',
            },
            '&::after': {
              borderBottom: 'none',
            },
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme: themeParam }) => ({
          '& .Mui-disabled.MuiInputBase-input': {
            WebkitTextFillColor: themeParam.palette.tertiary1,
          },
        }),
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme: themeParam }) => ({
          '& .Mui-disabled.MuiInputBase-input': {
            WebkitTextFillColor: themeParam.palette.tertiary1,
          },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme: themeParam }) => ({
          '& .MuiFormLabel-root.MuiInputLabel-root ': {
            color: themeParam.palette.mainFont,
            zIndex: 1,
            '&.Mui-error': {
              color: themeParam.palette.error.main,
            },
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme: themeParam }) => ({
          color: themeParam.palette.mainFont,
          '&.Mui-focused': {
            color: themeParam.palette.tertiary1,
          },
          '&.Mui-disabled': {
            color: themeParam.palette.mainFont,
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme: themeParam }) => ({
          '&:hover': {
            backgroundColor: themeParam.palette.mainBackground,
          },
          '&.Mui-selected': {
            backgroundColor: themeParam.palette.mainBackground,
            '&:hover': {
              backgroundColor: themeParam.palette.mainBackground,
            },
            '&.Mui-error:not(.Mui-focused)': {
              color: themeParam.palette.error.main,
              '&::after': {
                borderBottom: `1px solid ${themeParam.palette.error.main}`,
              },
            },
          },
        }),
      },
    },
    MuiAutoComplete: {
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-hasPopupIcon.MuiAutocomplete-root .MuiAutocomplete-inputRoot, .MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot':
            {
              paddingRight: '2px !important',
            },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterTouchDelay: 0,
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'primary',
      },
      styleOverrides: {
        root: ({ theme: themeParam }) => ({
          textTransform: 'none',
          borderRadius: rem(100),
          height: rem(50),
          boxShadow: 'none',
          whiteSpace: 'nowrap',
          fontSize: rem(15),
          fontWeight: themeParam.typography.fontWeightBold,
          letterSpacing: rem(1.2),
          paddingInline: '1.5rem',
          width: 'fit-content',
          [theme.breakpoints.up('sm')]: {
            paddingInline: '2rem',
          },
          [theme.breakpoints.up('md')]: {
            paddingInline: '3rem',
          },
          '&:disabled': {
            cursor: 'not-allowed',
          },
        }),
      },
      variants: [
        {
          props: { variant: 'marketingTertiary' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: themeParam.palette.common.white,
            color: themeParam.palette.tertiaryButtonFont,
            '&:hover': {
              backgroundColor: themeParam.palette.common.white,
              color: themeParam.palette.tertiaryButtonFont,
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: themeParam.palette.tertiary5,
              color: themeParam.palette.tertiary4,
              border: `${rem(1)} solid ${themeParam.palette.tertiary5}`,
            },
          }),
        },
        {
          props: { variant: 'marketingSecondary' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: themeParam.palette.marketing.otherButtonBackground,
            color: themeParam.palette.common.white,
            '&:hover': {
              color: themeParam.palette.common.white,
              backgroundColor: lighten(
                themeParam.palette.marketing.otherButtonBackground,
                0.2,
              ),
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: themeParam.palette.tertiary5,
              color: themeParam.palette.tertiary4,
              border: `${rem(1)} solid ${themeParam.palette.tertiary5}`,
            },
          }),
        },
        {
          props: { variant: 'primary' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: themeParam.palette.primaryButtonBg,
            color: themeParam.palette.primaryButtonFont,
            '&:hover': {
              backgroundColor: lighten(themeParam.palette.primaryButtonBg, 0.2),
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: themeParam.palette.tertiary5,
              color: themeParam.palette.tertiary4,
              border: `${rem(1)} solid ${themeParam.palette.tertiary5}`,
            },
          }),
        },
        {
          props: { variant: 'secondary' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: 'transparent',
            color: themeParam.palette.secondaryButtonFont,
            border: `${rem(1)} solid ${themeParam.palette.secondaryButtonFont}`,
            '&:hover': {
              backgroundColor: 'transparent',
              color: themeParam.palette.tertiary1,
              border: `${rem(1)} solid ${themeParam.palette.tertiary1}`,
              boxShadow: 'none',
              '& svg': {
                fill: themeParam.palette.tertiary1,
              },
            },
            '&:disabled': {
              backgroundColor: 'transparent',
              color: themeParam.palette.tertiary5,
              border: `${rem(1)} solid ${themeParam.palette.tertiary5}`,
            },
          }),
        },
        {
          props: { variant: 'secondaryDark' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: themeParam.palette.secondaryButtonFont,
            color: themeParam.palette.secondaryButtonBg,
            border: `${rem(1)} solid ${themeParam.palette.secondaryButtonBg}`,
            '&:hover': {
              backgroundColor: themeParam.palette.secondaryButtonFont,
              color: lighten(themeParam.palette.secondaryButtonBg, 0.3),
              border: `${rem(1)} solid ${lighten(themeParam.palette.secondaryButtonBg, 0.4)}`,
              boxShadow: 'none',
              '& svg': {
                fill: lighten(themeParam.palette.secondaryButtonBg, 0.4),
              },
            },
            '&:disabled': {
              backgroundColor: themeParam.palette.secondaryButtonFont,
              color: themeParam.palette.tertiary4,
              border: `${rem(1)} solid ${themeParam.palette.tertiary4}`,
            },
          }),
        },
        {
          props: { variant: 'tertiary1' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: 'transparent',
            color: themeParam.palette.mainFont,
            '&:hover, & svg': {
              backgroundColor: 'transparent',
              color: themeParam.palette.tertiary1,
              fill: themeParam.palette.tertiary1,
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: 'transparent',
              color: themeParam.palette.tertiary5,
            },
          }),
        },
        {
          props: { variant: 'tertiary2' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: themeParam.palette.tertiary1,
            color: themeParam.palette.common.white,
            '&:hover': {
              backgroundColor: darken(themeParam.palette.tertiary1, 0.2),
              color: themeParam.palette.common.white,
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: themeParam.palette.tertiary5,
              color: themeParam.palette.tertiary4,
              border: `${rem(1)} solid ${themeParam.palette.tertiary5}`,
            },
          }),
        },
        {
          props: { variant: 'primaryModern' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: themeParam.palette.primaryButtonBg,
            color: themeParam.palette.primaryButtonFont,
            borderRadius: rem(8),
            fontSize: rem(14),
            letterSpacing: 0,
            width: 'auto',
            height: 'auto',
            minWidth: 'initial',
            minHeight: 'initial',
            maxHeight: rem(40),
            '&&&': {
              paddingInline: rem(16),
              paddingBlock: rem(8),
            },
            '&:hover': {
              backgroundColor: lighten(themeParam.palette.primaryButtonBg, 0.2),
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: themeParam.palette.tertiary5,
              color: themeParam.palette.tertiary4,
              border: `${rem(1)} solid ${themeParam.palette.tertiary5}`,
            },
            '> .MuiLoadingButton-loadingIndicatorStart': {
              position: 'static',
              marginRight: rem(8),
            },
            '> .MuiLoadingButton-loadingIndicatorEnd': {
              position: 'static',
              marginLeft: rem(8),
            },
          }),
        },
        {
          props: { variant: 'secondaryModern' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: 'transparent',
            color: themeParam.palette.secondaryButtonFont,
            border: `${rem(1)} solid ${themeParam.palette.secondaryButtonFont}`,
            borderRadius: rem(8),
            fontSize: rem(14),
            letterSpacing: 0,
            width: 'auto',
            height: 'auto',
            minWidth: 'initial',
            minHeight: 'initial',
            maxHeight: rem(40),
            '&&&': {
              paddingInline: rem(16),
              paddingBlock: rem(8),
            },
            '&:hover': {
              backgroundColor: 'transparent',
              color: themeParam.palette.tertiary1,
              border: `${rem(1)} solid ${themeParam.palette.tertiary1}`,
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: 'transparent',
              color: themeParam.palette.tertiary5,
              border: `${rem(1)} solid ${themeParam.palette.tertiary5}`,
            },
            '> .MuiLoadingButton-loadingIndicatorStart': {
              position: 'static',
              marginRight: rem(8),
            },
          }),
        },
        {
          props: { variant: 'tertiary1Modern' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: themeParam.palette.tertiary1,
            color: getWhiteOrBlackTextColorByBackground(
              themeParam.palette.tertiary1,
              themeParam,
            ),
            borderRadius: rem(8),
            fontSize: rem(14),
            letterSpacing: 0,
            width: 'auto',
            height: 'auto',
            minWidth: 'initial',
            minHeight: 'initial',
            maxHeight: rem(40),
            '&&&': {
              paddingInline: rem(16),
              paddingBlock: rem(8),
            },
            '&:hover': {
              backgroundColor: lighten(themeParam.palette.tertiary1, 0.2),
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: lighten(themeParam.palette.tertiary1, 0.2),
              color: getWhiteOrBlackTextColorByBackground(
                themeParam.palette.tertiary1,
                themeParam,
              ),
              opacity: 0.7,
              border: `${rem(1)} solid transparent`,
            },
            '> .MuiLoadingButton-loadingIndicatorStart': {
              position: 'static',
              marginRight: rem(8),
            },
            '> .MuiLoadingButton-loadingIndicatorEnd': {
              position: 'static',
              marginLeft: 'auto',
            },
          }),
        },
        {
          props: { variant: 'tertiary1SecondaryModern' },
          style: ({ theme: themeParam }) => ({
            backgroundColor: themeParam.palette.common.white,
            color: themeParam.palette.tertiary1,
            borderRadius: rem(8),
            border: `${rem(1)} solid ${themeParam.palette.tertiary1}`,
            fontSize: rem(14),
            letterSpacing: 0,
            width: 'auto',
            height: 'auto',
            minWidth: 'initial',
            minHeight: 'initial',
            maxHeight: rem(40),
            '&&&': {
              paddingInline: rem(16),
              paddingBlock: rem(8),
            },
            '&:hover': {
              boxShadow: 'none',
            },
            '&:disabled': {
              borderColor: themeParam.palette.tertiary1,
              color: themeParam.palette.tertiary1,
              opacity: 0.7,
            },
            '> .MuiLoadingButton-loadingIndicatorStart': {
              position: 'static',
              marginRight: rem(8),
            },
            '> .MuiLoadingButton-loadingIndicatorEnd': {
              position: 'static',
              marginLeft: 'auto',
            },
          }),
        },
      ],
    },
    MuiModal: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 3,
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme: themeParam }) => ({
          color: themeParam.palette.secondary1,
          '&:hover': {
            color: themeParam.palette.tertiary1,
          },
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: ({ theme: themeParam }) => ({
          backgroundColor: lighten(themeParam.palette.tertiary1, 0.8),
        }),
        barColorPrimary: ({ theme: themeParam }) => ({
          backgroundColor: themeParam.palette.tertiary1,
        }),
      },
    },
    MuiTypography: {
      // No `marketing` default: MUI would hand `marketing={false}` to every Typography's DOM node
      // (a React warning on every page). Unset reads as false in the overrides below.
      styleOverrides: {
        root: ({ ownerState, marketing }) => {
          if (ownerState.variant === 'h2' && marketing) {
            return {
              '&.MuiTypography-h2': {
                fontWeight: 700,
                fontSize: rem(30),
                lineHeight: rem(40),
                letterSpacing: rem(1),
                textAlign: 'center',
                [theme.breakpoints.up('sm')]: {
                  fontSize: rem(40),
                  lineHeight: rem(50),
                },
              },
            }
          }

          if (ownerState.variant === 'h4' && marketing) {
            return {
              '&.MuiTypography-h4': {
                fontSize: rem(16),
                lineHeight: rem(18),
                letterSpacing: rem(3),
                fontWeight: 600,
                textTransform: 'uppercase',
                textAlign: 'center',
                color: ownerState.color || theme.palette.tertiary1,
                [theme.breakpoints.up('sm')]: {
                  fontSize: rem(14),
                  lineHeight: rem(16),
                },
              },
            }
          }

          return {}
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: (themeParam) => ({
        a: {
          color: themeParam.palette.secondary1,
        },
        body: {
          overflowX: 'hidden',
          '& .MuiAutocomplete-root': {
            width: '100%',
            '& .MuiFormControl-root.MuiTextField-root': {
              width: '100%',
            },
          },
          '& .MuiPaper-root': {
            borderRadius: 0,
          },
          '&::-webkit-scrollbar, & ::-webkit-scrollbar': {
            width: '0.6rem',
          },
          '&::-webkit-scrollbar-track, & ::-webkit-scrollbar-track': {
            backgroundColor: themeParam.palette.common.white,
          },
          '&::-webkit-scrollbar-thumb, & ::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(themeParam.palette.mainFont, 0.6),
            outline: 'none',
          },
          '& #account-side-nav': {
            '&::-webkit-scrollbar, & ::-webkit-scrollbar': {
              width: '0.3rem',
            },
            '&::-webkit-scrollbar-track, & ::-webkit-scrollbar-track': {
              backgroundColor: themeParam.palette.common.white,
            },
            '&::-webkit-scrollbar-thumb, & ::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(themeParam.palette.mainFont, 0.6),
              outline: 'none',
            },
          },
        },
      }),
    },
  },
} as ThemeOptions)

export default theme
